import React, {useState, useEffect, useRef, forwardRef, useImperativeHandle} from 'react'

import Alert from '../feedback/Alert.js'
import Panel from '../surfaces/Panel.js'
import ListItem from '../surfaces/ListItem.js'
import TextArea from './TextArea.js'
import S1 from '../typography/S1.js'
import P from '../typography/P.js'
import Input from './Input.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import {field} from './styles.js'

export interface EditorProps extends FieldProps, InvalidProps {
    value?: string
    // Only called on blur, with the edited value
    onChange?: (event: {target: {value: string}}) => void
    // Falsy entries (e.g. `condition && 'tag'`) are skipped
    tags?: Array<string | false | null | undefined>
    as?: 'input'
    comment?: React.ReactNode
}

export type EditorRef = FieldRef

// Both Input and TextArea implement FieldRef and expose getRef() returning the underlying DOM element
interface EditorFieldRef extends FieldRef {
    getRef: () => HTMLInputElement | HTMLTextAreaElement | null
}

const Editor = forwardRef<EditorRef, EditorProps>(function Editor(props, ref) {
    const {comment, label, info, disabled, readOnly, isInvalid, style} = props

    const [value, setValue] = useState(props.value)
    const [startAt, setStartAt] = useState(-1)
    const [mentionList, setMentionList] = useState<string[]>([])
    const [selectedMention, setSelectedMention] = useState<number>(undefined)
    const [top, setTop] = useState(0)
    const [left, setLeft] = useState(0)

    const textAreaRef = useRef<EditorFieldRef | null>(null)

    useImperativeHandle(ref, () => ({
        focus: () => textAreaRef.current?.focus(),
        blur: () => textAreaRef.current?.blur(),
        setInvalid: (isInvalid = true) => textAreaRef.current?.setInvalid(isInvalid)
    }), [])

    useEffect(() => {
        setValue(props.value)
    }, [props.value])

    const getTags = () => (props.tags || []).filter(Boolean) as string[]

    const hideLookup = (event?: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const selectionStart = (event?.target as HTMLInputElement | HTMLTextAreaElement | undefined)?.selectionStart

        if (!selectionStart || selectionStart !== startAt) {
            setMentionList([])
            setStartAt(-1)
        }
    }

    const insertNameIntoInput = (tag: string) => {
        const textArea = textAreaRef.current.getRef()

        // The typed mention runs from the '{' up to the caret
        const mentionSize = Math.max(0, textArea.selectionStart - startAt)

        const first = value.substr(0, startAt)
        const last = value.substr(startAt + mentionSize, value.length)

        setValue(`${first}${tag}}${last}`)
        setStartAt(-1)

        textArea.focus()
        hideLookup()

        // Place the caret after the inserted tag once the new value is rendered (Input syncs its value in an extra render)
        const caret = startAt + tag.length + 1
        requestAnimationFrame(() => textArea.setSelectionRange(caret, caret))
    }

    const updateMentionList = (value: string, startAt: number) => {
        const mention = extractMention(value, startAt)

        const filteredData = getTags().filter((tag) => tag.toLowerCase().includes(mention))
            .slice(0, 10)

        setMentionList(filteredData)
        setSelectedMention(0)
    }

    const onChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const {value = '', selectionStart} = event.target

        const character = value.substring(selectionStart - 1, selectionStart)

        if (character === '{' && getTags().length) {
            const textArea = textAreaRef.current.getRef()
            const {top, left} = getCaretCoordinates(textArea, textArea.selectionEnd)

            setValue(value)
            setStartAt(selectionStart)
            setMentionList(getTags())
            setSelectedMention(0)
            setTop(top)
            setLeft(left)

            return
        }

        if (character === ' ' || value.trim() === '') {
            setValue(value)
            hideLookup()
            return
        }

        if (startAt > -1 && selectionStart < startAt) { // The '{' was removed
            setValue(value)
            hideLookup()
            return
        }

        if (startAt > -1) {
            setValue(value)
            updateMentionList(value, startAt)
            return
        }

        setValue(value)
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (mentionList.length && (event.keyCode === 40 || event.which === 40 || event.keyCode === 38 || event.which === 38 || event.keyCode === 13 || event.which === 13)) {
            event.preventDefault()

            if ((event.keyCode === 40 || event.which === 40) && selectedMention < mentionList.length - 1) { // Down
                setSelectedMention(selectedMention + 1)
            }

            if ((event.keyCode === 38 || event.which === 38) && selectedMention > 0) { // Up
                setSelectedMention(selectedMention - 1)
            }

            const tag = mentionList[selectedMention]
            if ((event.keyCode === 13 || event.which === 13) && tag) { // Enter
                insertNameIntoInput(tag)
            }
        }
    }

    const onBlur = () => {
        hideLookup()

        // setTimeout(() => {
        // Only emit when edited, callers save on every call
        if (document.activeElement !== textAreaRef.current?.getRef() && value !== props.value) {
            props.onChange?.({target: {value}})
        }
        // }, 100)
    }

    const showTagsAlert = !!getTags().length && !props.as

    // The alerts sit right under the field. The last alert keeps some space below it, on top of the gap the
    // surrounding FormStack puts after the editor (30 in total, as before fields lost their margins)
    const alertsMarginBottom = 18

    // Positions the tag list against the field and grows like a field in a FormRow; the field itself gets the caller's style
    return (
        <div style={{position: 'relative', flex: style?.width ? undefined : 1, minWidth: 0}}>
            {!!mentionList.length &&
                <Panel
                    style={{
                        position: 'absolute',
                        zIndex: 10,
                        width: 200,
                        left: left,
                        top: top + 18
                    }}
                >
                    {mentionList.map((tag, index) => {
                        if (tag) {
                            return (
                                <ListItem
                                    size='sm'
                                    focused={index === selectedMention}
                                    key={`tag${index}`}
                                    // Keep focus in the field, so its blur doesn't close the list before the click lands
                                    onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                                    onClick={() => insertNameIntoInput(tag)}
                                >
                                    {tag}
                                </ListItem>
                            )
                        }
                    })}
                </Panel>
            }

            {props.as === 'input' ?
                <Input
                    label={label}
                    info={info}
                    value={value}
                    disabled={disabled}
                    readOnly={readOnly}
                    isInvalid={isInvalid}
                    style={style}
                    ref={(ref) => textAreaRef.current = ref}
                    onChange={onChange}
                    onBlur={onBlur}
                    onClick={hideLookup}
                    onKeyDown={onKeyDown}
                /> :
                <TextArea
                    label={label}
                    info={info}
                    value={value}
                    rows={10}
                    maxRows={field.editorMaxRows}
                    disabled={disabled}
                    readOnly={readOnly}
                    isInvalid={isInvalid}
                    style={style}
                    ref={(ref) => textAreaRef.current = ref}
                    onChange={onChange}
                    onBlur={onBlur}
                    onClick={hideLookup}
                    onKeyDown={onKeyDown}
                />
            }

            {comment &&
                <Alert variant='info' style={{marginBottom: showTagsAlert ? 20 : alertsMarginBottom}}>
                    <P>{comment}</P>
                </Alert>
            }

            {showTagsAlert &&
                <Alert variant='info' style={{marginBottom: alertsMarginBottom}}>
                    <S1>Gebruik <code>{'{tags}'}</code> in je bericht</S1>
                    <P>Type een accolade <code>{'{'}</code> en selecteer een tag uit het keuzemenu</P>
                </Alert>
            }
        </div>
    )
})

export default Editor

const extractMention = (value: string, startAt: number) => {
    let mention = value.substring(startAt, value.length)
    const whiteSpaceIndex = mention.search(/\s/)
    const endAt = whiteSpaceIndex > -1 ? whiteSpaceIndex : value.length
    mention = mention.substring(0, endAt)
    return mention.toLowerCase()
}

const getCaretCoordinates = (element: HTMLInputElement | HTMLTextAreaElement, position: number, options?: {debug?: boolean}) => {
    if (!isBrowser) {
        throw new Error('textarea-caret-position#getCaretCoordinates should only be called in a browser')
    }

    const debug = options && options.debug || false
    if (debug) {
        const el = document.querySelector('#input-textarea-caret-position-mirror-div')
        if (el) el.parentNode.removeChild(el)
    }

    // The mirror div will replicate the textarea's style
    const div = document.createElement('div')
    div.id = 'input-textarea-caret-position-mirror-div'
    document.body.appendChild(div)

    const style = div.style
    const computed = window.getComputedStyle(element)
    const isInput = element.nodeName === 'INPUT'

    // Default textarea styles
    style.whiteSpace = 'pre-wrap'
    if (!isInput) {
        style.wordWrap = 'break-word'
    } // only for textarea-s

    // Position off-screen
    style.position = 'absolute' // required to return coordinates properly
    if (!debug) {
        style.visibility = 'hidden'
    } // not 'display: none' because we want rendering

    // Transfer the element's properties to the div
    properties.forEach((prop) => {
        if (isInput && prop === 'lineHeight') {
        // Special case for <input>s because text is rendered centered and line height may be != height
            if (computed.boxSizing === 'border-box') {
                const height = parseInt(computed.height)
                const outerHeight =
                parseInt(computed.paddingTop) +
                parseInt(computed.paddingBottom) +
                parseInt(computed.borderTopWidth) +
                parseInt(computed.borderBottomWidth)
                const targetHeight = outerHeight + parseInt(computed.lineHeight)
                if (height > targetHeight) {
                    style.lineHeight = height - outerHeight + 'px'
                } else if (height === targetHeight) {
                    style.lineHeight = computed.lineHeight
                } else {
                    style.lineHeight = '0'
                }
            } else {
                style.lineHeight = computed.height
            }
        } else {
            style[prop] = computed[prop]
        }
    })

    if (isFirefox) {
        // Firefox lies about the overflow property for textareas: https://bugzilla.mozilla.org/show_bug.cgi?id=984275
        if (element.scrollHeight > parseInt(computed.height)) {
            style.overflowY = 'scroll'
        }
    } else {
        style.overflow = 'hidden' // for Chrome to not render a scrollbar; IE keeps overflowY = 'scroll'
    }

    div.textContent = element.value.substring(0, position)
    // The second special handling for input type="text" vs textarea:
    // spaces need to be replaced with non-breaking spaces - http://stackoverflow.com/a/13402035/1269037
    if (isInput) {
        div.textContent = div.textContent.replace(/\s/g, ' ')
    }

    const span = document.createElement('span')
    // Wrapping must be replicated *exactly*, including when a long word gets
    // onto the next line, with whitespace at the end of the line before (#7).
    // The  *only* reliable way to do that is to copy the *entire* rest of the
    // textarea's content into the <span> created at the caret position.
    // For inputs, just '.' would be enough, but no need to bother.
    span.textContent = element.value.substring(position) || '.' // || because a completely empty faux span doesn't render at all
    div.appendChild(span)

    const coordinates = {
        top: span.offsetTop + parseInt(computed['borderTopWidth']),
        left: span.offsetLeft + parseInt(computed['borderLeftWidth']),
        height: parseInt(computed['lineHeight'])
    }

    if (debug) {
        span.style.backgroundColor = '#aaa'
    } else {
        document.body.removeChild(div)
    }

    if (element.scrollLeft) {
        coordinates.left = coordinates.left - element.scrollLeft
    }
    if (element.scrollHeight) {
        coordinates.top = coordinates.top - element.scrollTop
    }

    return coordinates
}

const properties = [
    'direction', // RTL support
    'boxSizing',
    'width', // on Chrome and IE, exclude the scrollbar, so the mirror div wraps exactly as the textarea does
    'height',
    'overflowX',
    'overflowY', // copy the scrollbar for IE

    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderStyle',

    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',

    // https://developer.mozilla.org/en-US/docs/Web/CSS/font
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',

    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration', // might not make a difference, but better be safe

    'letterSpacing',
    'wordSpacing',

    'tabSize',
    'MozTabSize'
]

const isBrowser = (typeof window !== 'undefined')
const isFirefox = isBrowser && 'mozInnerScreenX' in window
